/**
 * DesktopHint — мягкий баннер вверху экрана для пользователей которые
 * открыли игру с десктопа/большого экрана.
 *
 * JB 2026-05-26: «у нас на телефоны ток ориентир, с пк играть не
 * предлагаем». Игра спроектирована под одну руку, портретный viewport,
 * touch-input. На широких экранах UI растягивается, рандомные сенсорные
 * эффекты теряют точку фокуса, тапы мышкой не несут той же телесности.
 *
 * Решение — не блокировать (тестеры JB сидят за PC), но мягко
 * предложить открыть с телефона. Один раз, dismissable, сохраняется в
 * localStorage.
 *
 * Триггер: matchMedia('(min-width: 900px) and (pointer: fine)') —
 * широкий viewport + точный pointer (мышь, не палец). Touch-screen
 * laptop при этом не триггерится (pointer: coarse).
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import styles from './DesktopHint.module.css';

const STORAGE_KEY = 'sai-desktop-hint-dismissed-v1';
const MEDIA_QUERY = '(min-width: 900px) and (pointer: fine)';

export function DesktopHint(): JSX.Element {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Проверяем localStorage один раз — если игрок уже видел баннер,
    // не показываем снова. Локальный — баннер per-устройство, что и нужно.
    if (typeof window === 'undefined') return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch {
      // Private mode / quota — пропускаем persistence, баннер каждый раз.
    }
    const mq = window.matchMedia(MEDIA_QUERY);
    if (mq.matches) setVisible(true);
    // При изменении viewport (например, поворот desktop monitor —
    // редко, но возможно) — реагируем.
    const onChange = (e: MediaQueryListEvent): void => {
      if (!e.matches) setVisible(false);
    };
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  function dismiss(): void {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={styles.banner}
          role="status"
          aria-label="Совет открыть на телефоне"
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.8, 0.3, 1] }}
        >
          <span className={styles.text}>
            игра сделана для одной руки — лучше открыть с телефона.
          </span>
          <button type="button" className={styles.dismiss} onClick={dismiss} aria-label="Закрыть">
            ✕
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
